import React, {useState, useEffect} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Mail,
    Send,
    Clock,
    CheckCircle,
    XCircle,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Plus,
    Calendar,
    Filter,
    Eye,
    StopCircle,
    PlayCircle,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    X,
    Settings,
    Zap,
    Trash2  // Add this if not already present
} from 'lucide-react';

const BulkEmailPage = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [cancelingId, setCancelingId] = useState(null);
    const [editorContent, setEditorContent] = useState('');
    // Add these state variables near the top with other useState
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [isDeletingSingle, setIsDeletingSingle] = useState(null);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [bulkDeleteFilters, setBulkDeleteFilters] = useState({
        status_in: [],
        before_date: '',
        older_than_days: '',
        force: false
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCampaigns, setTotalCampaigns] = useState(0);

    // Form state
    const [formData, setFormData] = useState({
        subject: '',
        delay_minutes: 3,
        filter_sect: '',
        filter_plan: '',
        filter_approval: 'Approved',
        test_mode: false,
        limit: 25,
        batch_size: 100,
        inter_batch_sleep_ms: 500
    });

    // Quill modules configuration
    const modules = {
        toolbar: [
            [{'header': [1, 2, 3, false]}],
            ['bold', 'italic', 'underline', 'strike'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            [{'align': []}],
            [{'color': []}, {'background': []}],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'align',
        'color', 'background',
        'link'
    ];

    useEffect(() => {
        fetchCampaigns();
        const interval = setInterval(fetchCampaigns, 20000);
        return () => clearInterval(interval);
    }, [currentPage, pageSize]);

    const fetchCampaigns = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(
                `https://api.digitalaalim.com/admin/bulk-email/?page=${currentPage}&page_size=${pageSize}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch campaigns: ${response.statusText}`);
            }

            const data = await response.json();
            setCampaigns(data.items || []);
            setTotalCampaigns(data.total || 0);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const createCampaign = async () => {
        if (!formData.subject) {
            alert('Please enter a subject line');
            return;
        }

        if (!editorContent.trim() || editorContent === '<p><br></p>') {
            alert('Please enter email content');
            return;
        }

        setIsCreating(true);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const payload = {
                ...formData,
                body_html: editorContent,
                filter_sect: formData.filter_sect || null,
                filter_plan: formData.filter_plan || null,
            };

            const response = await fetch('https://api.digitalaalim.com/admin/bulk-email/launch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Failed to create campaign: ${response.statusText}`);
            }

            const data = await response.json();
            alert(`Campaign "${data.subject}" scheduled successfully!`);
            setShowCreateModal(false);
            resetForm();
            fetchCampaigns();
        } catch (error) {
            console.error('Error creating campaign:', error);
            alert(`Failed to create campaign: ${error.message}`);
        } finally {
            setIsCreating(false);
        }
    };

    const viewCampaignDetails = async (campaignId) => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(
                `https://api.digitalaalim.com/admin/bulk-email/${campaignId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch campaign details`);
            }

            const data = await response.json();
            // console.log("compaign details data: ", data);
            setSelectedCampaign(data);
            setShowDetailModal(true);
        } catch (error) {
            alert(`Failed to load campaign details: ${error.message}`);
        }
    };

    const cancelCampaign = async (campaignId) => {
        if (!window.confirm('Are you sure you want to cancel this campaign?')) return;

        setCancelingId(campaignId);
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(
                `https://api.digitalaalim.com/admin/bulk-email/${campaignId}/cancel`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to cancel campaign`);
            }

            alert('Campaign cancellation requested');
            fetchCampaigns();
        } catch (error) {
            alert(`Failed to cancel campaign: ${error.message}`);
        } finally {
            setCancelingId(null);
        }
    };

    // Single Campaign Delete
    const deleteSingleCampaign = async (campaignId, force = false) => {
        const confirmMessage = force
            ? 'Are you sure you want to force delete this campaign? It will be canceled first if running.'
            : 'Are you sure you want to delete this campaign?';

        if (!window.confirm(confirmMessage)) return;

        setIsDeletingSingle(campaignId);
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(
                `https://api.digitalaalim.com/admin/bulk-email/${campaignId}?force=${force}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                if (response.status === 409) {
                    const shouldForce = window.confirm(
                        'This campaign is running/scheduled. Do you want to force delete it?'
                    );
                    if (shouldForce) {
                        return deleteSingleCampaign(campaignId, true);
                    }
                    throw new Error('Cannot delete running/scheduled campaign without force');
                }
                throw new Error(`Failed to delete campaign: ${response.statusText}`);
            }

            alert('Campaign deleted successfully');
            fetchCampaigns();
            setSelectedCampaigns(prev => prev.filter(id => id !== campaignId));
        } catch (error) {
            console.error('Error deleting campaign:', error);
            alert(`Failed to delete campaign: ${error.message}`);
        } finally {
            setIsDeletingSingle(null);
        }
    };

// Bulk Delete Campaigns
    const bulkDeleteCampaigns = async () => {
        if (selectedCampaigns.length === 0 &&
            !bulkDeleteFilters.status_in.length &&
            !bulkDeleteFilters.before_date &&
            !bulkDeleteFilters.older_than_days) {
            alert('Please select campaigns or set filters for bulk deletion');
            return;
        }

        const confirmMessage = `Are you sure you want to delete ${
            selectedCampaigns.length > 0
                ? `${selectedCampaigns.length} selected campaign(s)`
                : 'campaigns matching the filters'
        }?`;

        if (!window.confirm(confirmMessage)) return;

        setIsBulkDeleting(true);
        try {
            const accessToken = localStorage.getItem('accessToken');

            const payload = {
                force: bulkDeleteFilters.force
            };

            if (selectedCampaigns.length > 0) {
                payload.ids = selectedCampaigns;
            }
            if (bulkDeleteFilters.status_in.length > 0) {
                payload.status_in = bulkDeleteFilters.status_in;
            }
            if (bulkDeleteFilters.before_date) {
                payload.before_date = bulkDeleteFilters.before_date;
            }
            if (bulkDeleteFilters.older_than_days) {
                payload.older_than_days = parseInt(bulkDeleteFilters.older_than_days);
            }

            const response = await fetch(
                'https://api.digitalaalim.com/admin/bulk-email/',
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                if (response.status === 409) {
                    const shouldForce = window.confirm(
                        'Some campaigns are running/scheduled. Do you want to force delete them?'
                    );
                    if (shouldForce) {
                        bulkDeleteFilters.force = true;
                        setBulkDeleteFilters({...bulkDeleteFilters});
                        return bulkDeleteCampaigns();
                    }
                    throw new Error('Cannot delete running/scheduled campaigns without force');
                }
                throw new Error(`Failed to bulk delete campaigns: ${response.statusText}`);
            }

            const result = await response.json();
            alert(`Successfully deleted campaigns. Details: ${JSON.stringify(result)}`);
            setShowBulkDeleteModal(false);
            setSelectedCampaigns([]);
            setBulkDeleteFilters({
                status_in: [],
                before_date: '',
                older_than_days: '',
                force: false
            });
            fetchCampaigns();
        } catch (error) {
            console.error('Error bulk deleting campaigns:', error);
            alert(`Failed to bulk delete campaigns: ${error.message}`);
        } finally {
            setIsBulkDeleting(false);
        }
    };

// Toggle campaign selection
    const toggleCampaignSelection = (campaignId) => {
        setSelectedCampaigns(prev =>
            prev.includes(campaignId)
                ? prev.filter(id => id !== campaignId)
                : [...prev, campaignId]
        );
    };

// Select all campaigns on current page
    const toggleSelectAll = () => {
        if (selectedCampaigns.length === campaigns.length) {
            setSelectedCampaigns([]);
        } else {
            setSelectedCampaigns(campaigns.map(c => c._id));
        }
    };

    const resetForm = () => {
        setFormData({
            subject: '',
            delay_minutes: 3,
            filter_sect: '',
            filter_plan: '',
            filter_approval: 'Approved',
            test_mode: false,
            limit: 25,
            batch_size: 100,
            inter_batch_sleep_ms: 500
        });
        setEditorContent('');
    };

    const getStatusBadge = (status) => {
        const badges = {
            scheduled: {bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock},
            running: {bg: 'bg-yellow-100', text: 'text-yellow-800', icon: PlayCircle},
            completed: {bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle},
            failed: {bg: 'bg-red-100', text: 'text-red-800', icon: XCircle},
            canceled: {bg: 'bg-gray-100', text: 'text-gray-800', icon: StopCircle}
        };

        const config = badges[status] || badges.scheduled;
        const Icon = config.icon;

        return (
            <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
                <Icon size={12} className="mr-1"/>
                {status.toUpperCase()}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const totalPages = Math.max(1, Math.ceil(totalCampaigns / pageSize));
    const goToPage = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    const stats = {
        total: campaigns.length,
        scheduled: campaigns.filter(c => c.status === 'scheduled').length,
        running: campaigns.filter(c => c.status === 'running').length,
        completed: campaigns.filter(c => c.status === 'completed').length,
        failed: campaigns.filter(c => c.status === 'failed').length
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                <div className="p-2 bg-blue-600 rounded-xl">
                                    <Mail className="text-white" size={28}/>
                                </div>
                                Bulk Email Campaigns
                            </h1>
                            <p className="mt-2 text-gray-600">Create and manage email campaigns for your users</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={fetchCampaigns}
                                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all shadow-sm"
                            >
                                <RefreshCw size={16} className="mr-2"/>
                                Refresh
                            </button>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                            >
                                <Plus size={18} className="mr-2"/>
                                New Campaign
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards - same as before */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div
                        className="bg-white rounded-xl shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Mail size={20} className="text-blue-600"/>
                            </div>
                        </div>
                    </div>
                    <div
                        className="bg-white rounded-xl shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Scheduled</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Clock size={20} className="text-blue-600"/>
                            </div>
                        </div>
                    </div>
                    <div
                        className="bg-white rounded-xl shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Running</p>
                                <p className="text-2xl font-bold text-yellow-600">{stats.running}</p>
                            </div>
                            <div className="p-3 bg-yellow-100 rounded-lg">
                                <PlayCircle size={20} className="text-yellow-600"/>
                            </div>
                        </div>
                    </div>
                    <div
                        className="bg-white rounded-xl shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Completed</p>
                                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-lg">
                                <CheckCircle size={20} className="text-green-600"/>
                            </div>
                        </div>
                    </div>
                    <div
                        className="bg-white rounded-xl shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Failed</p>
                                <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-lg">
                                <XCircle size={20} className="text-red-600"/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Actions Bar */}
                {selectedCampaigns.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl shadow-md p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CheckCircle size={20} className="text-blue-600"/>
                                <span className="text-sm font-medium text-gray-900">
                                    {selectedCampaigns.length} campaign(s) selected
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowBulkDeleteModal(true)}
                                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                                >
                                    <Trash2 size={16} className="mr-2"/>
                                    Bulk Delete
                                </button>
                                <button
                                    onClick={() => setSelectedCampaigns([])}
                                    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Campaigns Table - same as before */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-md">
                        <Loader2 size={40} className="animate-spin text-blue-600"/>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-xl shadow-md">
                        <div className="flex items-center">
                            <AlertTriangle size={24} className="text-red-500 mr-3"/>
                            <div>
                                <h3 className="text-red-800 font-semibold">Error loading campaigns</h3>
                                <p className="text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-50">
                                <tr>
                                    <th className="px-6 py-4 text-left">
                                        <input
                                            type="checkbox"
                                            checked={selectedCampaigns.length === campaigns.length && campaigns.length > 0}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Campaign
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Schedule
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Progress
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Filters
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {campaigns.length > 0 ? (
                                    campaigns.map((campaign) => (
                                        <tr key={campaign._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCampaigns.includes(campaign._id)}
                                                    onChange={() => toggleCampaignSelection(campaign._id)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-start">
                                                    <div
                                                        className="flex-shrink-0 h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                        <Send size={18} className="text-blue-600"/>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div
                                                            className="text-sm font-semibold text-gray-900">{campaign.subject}</div>
                                                        <div className="text-xs text-gray-500">
                                                            Created: {formatDate(campaign.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(campaign.status)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    <div className="flex items-center">
                                                        <Calendar size={14} className="mr-1 text-gray-400"/>
                                                        {formatDate(campaign.scheduled_for)}
                                                    </div>
                                                    {campaign.started_at && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            Started: {formatDate(campaign.started_at)}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between text-xs">
                                                            <span className="text-gray-600">
                                                                {campaign.sent_count} / {campaign.total_recipients}
                                                            </span>
                                                        <span className="text-gray-500">
                                                                {campaign.total_recipients > 0
                                                                    ? Math.round((campaign.sent_count / campaign.total_recipients) * 100)
                                                                    : 0}%
                                                            </span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full transition-all"
                                                            style={{
                                                                width: `${campaign.total_recipients > 0
                                                                    ? (campaign.sent_count / campaign.total_recipients) * 100
                                                                    : 0}%`
                                                            }}
                                                        />
                                                    </div>
                                                    {campaign.failed_count > 0 && (
                                                        <div className="text-xs text-red-600">
                                                            {campaign.failed_count} failed
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {campaign.filters?.sect && (
                                                        <span
                                                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800">
                                                                <Filter size={10} className="mr-1"/>
                                                            {campaign.filters.sect}
                                                            </span>
                                                    )}
                                                    {campaign.filters?.plan && (
                                                        <span
                                                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                                                                {campaign.filters.plan}
                                                            </span>
                                                    )}
                                                    {campaign.filters?.approval && (
                                                        <span
                                                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-green-100 text-green-800">
                                                                {campaign.filters.approval}
                                                            </span>
                                                    )}
                                                    {!campaign.filters?.sect && !campaign.filters?.plan && (
                                                        <span className="text-xs text-gray-400">No filters</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => viewCampaignDetails(campaign._id)}
                                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                                                    >
                                                        <Eye size={14} className="mr-1"/>
                                                        View
                                                    </button>
                                                    {(campaign.status === 'scheduled' || campaign.status === 'running') && (
                                                        <button
                                                            onClick={() => cancelCampaign(campaign._id)}
                                                            disabled={cancelingId === campaign._id}
                                                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50"
                                                        >
                                                            {cancelingId === campaign._id ? (
                                                                <Loader2 size={14} className="mr-1 animate-spin"/>
                                                            ) : (
                                                                <StopCircle size={14} className="mr-1"/>
                                                            )}
                                                            Cancel
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteSingleCampaign(campaign._id, false)}
                                                        disabled={isDeletingSingle === campaign._id}
                                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                                                    >
                                                        {isDeletingSingle === campaign._id ? (
                                                            <Loader2 size={14} className="mr-1 animate-spin"/>
                                                        ) : (
                                                            <Trash2 size={14} className="mr-1"/>
                                                        )}
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center">
                                                <Mail size={48} className="text-gray-300 mb-4"/>
                                                <p className="text-gray-500 text-lg">No campaigns yet</p>
                                                <p className="text-gray-400 text-sm mt-1">Create your first campaign to
                                                    get started</p>
                                                <button
                                                    onClick={() => setShowCreateModal(true)}
                                                    className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                >
                                                    <Plus size={16} className="mr-2"/>
                                                    Create Campaign
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalCampaigns > 0 && (
                            <div
                                className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t">
                                <div className="text-sm text-gray-700">
                                    Showing <span
                                    className="font-semibold">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                    <span
                                        className="font-semibold">{Math.min(currentPage * pageSize, totalCampaigns)}</span> of{' '}
                                    <span className="font-semibold">{totalCampaigns}</span> campaigns
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => goToPage(1)}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronsLeft size={16}/>
                                    </button>
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft size={16}/>
                                    </button>
                                    <span className="px-4 py-1 text-sm text-gray-700">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight size={16}/>
                                    </button>
                                    <button
                                        onClick={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronsRight size={16}/>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Create Campaign Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">
                            <div
                                className="bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl flex-shrink-0">
                                <h2 className="text-xl font-bold text-white flex items-center">
                                    <Zap size={24} className="mr-2"/>
                                    Create New Campaign
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="text-white hover:bg-blue-700 rounded-lg p-1 transition-colors"
                                >
                                    <X size={24}/>
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto flex-1">
                                <div className="space-y-5">
                                    {/* Email Content */}
                                    <div className="space-y-3">
                                        <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                            <Mail size={16} className="mr-2 text-blue-600"/>
                                            Email Content
                                        </h3>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Subject Line *
                                            </label>
                                            <input
                                                type="text"
                                                maxLength={200}
                                                value={formData.subject}
                                                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                                placeholder="Enter email subject..."
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Email Body *
                                            </label>

                                            {/* React Quill Editor */}
                                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editorContent}
                                                    onChange={setEditorContent}
                                                    modules={modules}
                                                    formats={formats}
                                                    placeholder="Write your email content here..."
                                                    style={{height: '200px'}}
                                                />
                                            </div>
                                            <div className="h-12"></div>
                                            {/* Spacer for Quill toolbar */}
                                            <p className="mt-1 text-xs text-gray-500">Format your text with the toolbar
                                                above</p>
                                        </div>
                                    </div>

                                    {/* Filters */}
                                    <div className="space-y-3 border-t pt-4">
                                        <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                            <Filter size={16} className="mr-2 text-blue-600"/>
                                            Recipient Filters
                                        </h3>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                                <label
                                                    className="block text-sm font-medium text-gray-700 mb-1.5">Sect</label>
                                                <input
                                                    type="text"
                                                    value={formData.filter_sect}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        filter_sect: e.target.value
                                                    })}
                                                    placeholder="e.g., Hanafi"
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div>
                                                <label
                                                    className="block text-sm font-medium text-gray-700 mb-1.5">Plan</label>
                                                <select
                                                    value={formData.filter_plan}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        filter_plan: e.target.value
                                                    })}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="">All Plans</option>
                                                    <option value="FREE">FREE</option>
                                                    <option value="STANDARD">STANDARD</option>
                                                    <option value="PREMIUM">PREMIUM</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Approval
                                                    Status</label>
                                                <select
                                                    value={formData.filter_approval}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        filter_approval: e.target.value
                                                    })}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="Approved">Approved</option>
                                                    <option value="Pending">Pending</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Schedule & Settings */}
                                    <div className="space-y-3 border-t pt-4">
                                        <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                            <Settings size={16} className="mr-2 text-blue-600"/>
                                            Schedule & Settings
                                        </h3>

                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Delay (min)
                                                </label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={60}
                                                    value={formData.delay_minutes}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        delay_minutes: parseInt(e.target.value) || 0
                                                    })}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Batch Size
                                                </label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={1000}
                                                    value={formData.batch_size}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        batch_size: parseInt(e.target.value) || 100
                                                    })}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Sleep (ms)
                                                </label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={60000}
                                                    value={formData.inter_batch_sleep_ms}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        inter_batch_sleep_ms: parseInt(e.target.value) || 500
                                                    })}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>

                                        {/* Test Mode */}
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.test_mode}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        test_mode: e.target.checked
                                                    })}
                                                    className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                                                />
                                                <span className="ml-2 text-sm font-medium text-gray-900">
                                                    Test Mode (Limit Recipients)
                                                </span>
                                            </label>
                                            {formData.test_mode && (
                                                <div className="mt-2">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={2000}
                                                        value={formData.limit}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            limit: parseInt(e.target.value) || 25
                                                        })}
                                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                        placeholder="Max recipients"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div
                                className="flex justify-end gap-3 border-t px-6 py-4 bg-gray-50 rounded-b-xl flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={createCampaign}
                                    disabled={isCreating}
                                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md disabled:opacity-50 font-medium flex items-center"
                                >
                                    {isCreating ? (
                                        <>
                                            <Loader2 size={18} className="mr-2 animate-spin"/>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={18} className="mr-2"/>
                                            Launch Campaign
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Detail Modal */}
                {showDetailModal && selectedCampaign && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div
                                className="sticky top-0 bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
                                <h2 className="text-xl font-bold text-white">Campaign Details</h2>
                                <button
                                    onClick={() => {
                                        setShowDetailModal(false);
                                        setSelectedCampaign(null);
                                    }}
                                    className="text-white hover:bg-blue-700 rounded-lg p-1 transition-colors"
                                >
                                    <X size={24}/>
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">{selectedCampaign.subject}</h3>
                                    <div className="mt-2">{getStatusBadge(selectedCampaign.status)}</div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <p className="text-sm text-gray-600">Total Recipients</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">{selectedCampaign.total_recipients}</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4">
                                        <p className="text-sm text-gray-600">Sent</p>
                                        <p className="text-2xl font-bold text-green-600 mt-1">{selectedCampaign.sent_count}</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4">
                                        <p className="text-sm text-gray-600">Failed</p>
                                        <p className="text-2xl font-bold text-red-600 mt-1">{selectedCampaign.failed_count}</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <p className="text-sm text-gray-600">Success Rate</p>
                                        <p className="text-2xl font-bold text-blue-600 mt-1">
                                            {selectedCampaign.sent_count + selectedCampaign.failed_count > 0
                                                ? Math.round((selectedCampaign.sent_count / (selectedCampaign.sent_count + selectedCampaign.failed_count)) * 100)
                                                : 0}%
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <h4 className="font-semibold text-gray-900">Timeline</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Created:</span>
                                            <span
                                                className="font-medium">{formatDate(selectedCampaign.created_at)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Scheduled For:</span>
                                            <span
                                                className="font-medium">{formatDate(selectedCampaign.scheduled_for)}</span>
                                        </div>
                                        {selectedCampaign.started_at && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Started:</span>
                                                <span
                                                    className="font-medium">{formatDate(selectedCampaign.started_at)}</span>
                                            </div>
                                        )}
                                        {selectedCampaign.finished_at && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Finished:</span>
                                                <span
                                                    className="font-medium">{formatDate(selectedCampaign.finished_at)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {Object.keys(selectedCampaign.filters || {}).length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="font-semibold text-gray-900">Active Filters</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedCampaign.filters.sect && (
                                                <span
                                                    className="px-3 py-1 bg-purple-100 text-purple-800 rounded-lg text-sm">
                                                    Sect: {selectedCampaign.filters.sect}
                                                </span>
                                            )}
                                            {selectedCampaign.filters.plan && (
                                                <span
                                                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-sm">
                                                    Plan: {selectedCampaign.filters.plan}
                                                </span>
                                            )}
                                            {selectedCampaign.filters.approval && (
                                                <span
                                                    className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm">
                                                    Status: {selectedCampaign.filters.approval}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedCampaign.error && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <h4 className="font-semibold text-red-900 mb-2">Error</h4>
                                        <p className="text-sm text-red-700">{selectedCampaign.error}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Delete Modal */}
                {showBulkDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
                            <div className="bg-red-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
                                <h2 className="text-xl font-bold text-white flex items-center">
                                    <Trash2 size={24} className="mr-2"/>
                                    Bulk Delete Campaigns
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowBulkDeleteModal(false);
                                        setBulkDeleteFilters({
                                            status_in: [],
                                            before_date: '',
                                            older_than_days: '',
                                            force: false
                                        });
                                    }}
                                    className="text-white hover:bg-red-700 rounded-lg p-1 transition-colors"
                                >
                                    <X size={24}/>
                                </button>
                            </div>

                            <div className="p-6 space-y-5">
                                {selectedCampaigns.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <p className="text-sm text-gray-900">
                                            <strong>{selectedCampaigns.length}</strong> campaign(s) selected for
                                            deletion
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                        <Filter size={16} className="mr-2 text-red-600"/>
                                        Additional Filters (Optional)
                                    </h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Filter by Status
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {['scheduled', 'running', 'completed', 'failed', 'canceled'].map(status => (
                                                <label key={status} className="inline-flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={bulkDeleteFilters.status_in.includes(status)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setBulkDeleteFilters({
                                                                    ...bulkDeleteFilters,
                                                                    status_in: [...bulkDeleteFilters.status_in, status]
                                                                });
                                                            } else {
                                                                setBulkDeleteFilters({
                                                                    ...bulkDeleteFilters,
                                                                    status_in: bulkDeleteFilters.status_in.filter(s => s !== status)
                                                                });
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                                    />
                                                    <span
                                                        className="ml-2 text-sm text-gray-700 capitalize">{status}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Before Date
                                            </label>
                                            <input
                                                type="datetime-local"
                                                value={bulkDeleteFilters.before_date}
                                                onChange={(e) => setBulkDeleteFilters({
                                                    ...bulkDeleteFilters,
                                                    before_date: e.target.value
                                                })}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Older Than (Days)
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={bulkDeleteFilters.older_than_days}
                                                onChange={(e) => setBulkDeleteFilters({
                                                    ...bulkDeleteFilters,
                                                    older_than_days: e.target.value
                                                })}
                                                placeholder="e.g., 30"
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <label className="flex items-start cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={bulkDeleteFilters.force}
                                                onChange={(e) => setBulkDeleteFilters({
                                                    ...bulkDeleteFilters,
                                                    force: e.target.checked
                                                })}
                                                className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 mt-0.5"
                                            />
                                            <div className="ml-3">
                                <span className="text-sm font-medium text-gray-900">
                                    Force Delete
                                </span>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    If enabled, running/scheduled campaigns will be canceled first, then
                                                    deleted
                                                </p>
                                            </div>
                                        </label>
                                    </div>

                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <AlertTriangle size={20} className="text-red-600 mt-0.5 flex-shrink-0"/>
                                            <div className="ml-3">
                                                <p className="text-sm text-red-800 font-medium">Warning</p>
                                                <p className="text-xs text-red-700 mt-1">
                                                    This action cannot be undone. Deleted campaigns will be permanently
                                                    removed.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 border-t px-6 py-4 bg-gray-50 rounded-b-xl">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowBulkDeleteModal(false);
                                        setBulkDeleteFilters({
                                            status_in: [],
                                            before_date: '',
                                            older_than_days: '',
                                            force: false
                                        });
                                    }}
                                    className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={bulkDeleteCampaigns}
                                    disabled={isBulkDeleting}
                                    className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-md disabled:opacity-50 font-medium flex items-center"
                                >
                                    {isBulkDeleting ? (
                                        <>
                                            <Loader2 size={18} className="mr-2 animate-spin"/>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={18} className="mr-2"/>
                                            Delete Campaigns
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkEmailPage;